import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppNotification } from '../../../types/notifications/Notification';
import {
  getNotifications,
  markNotificationsSeen,
} from '../../../services/notifications/NotificationService';
import { NOTIFICATIONS_QUERY_KEY } from '../../query/notifications/keys';
import { useAccount } from '../../../contexts/account/AccountContexts';

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { accountResponse } = useAccount();
  // Notyfikacje są KONTOWE (rozliczenia, stan konta, ogłoszenia administratora), a gość jest tylko
  // widzem cudzego konta — nie dostaje ich ani w dzwonku, ani w odznace przy Ustawieniach. Bramka
  // siedzi tu, a nie w komponentach, bo hooka woła też `useBillingIssues` (odpytywanie co 10 s).
  const isGuest = accountResponse?.role === 'GUEST';

  const { data: notifications = [], isLoading } = useQuery<AppNotification[]>({
    queryKey: [NOTIFICATIONS_QUERY_KEY],
    queryFn: getNotifications,
    enabled: !!accountResponse && !isGuest,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    // Poll so newly-created notifications (admin broadcasts, account-state changes) show up on
    // their own. Only runs while the tab is focused.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const unreadCount = notifications.filter((n) => !n.seen).length;

  const { mutate: markSeen } = useMutation({
    // `keys` are the per-notification mark-seen keys (n.key, falling back to n.type).
    mutationFn: (keys: string[]) => markNotificationsSeen(keys),
    onMutate: async (keys) => {
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });

      const previous = queryClient.getQueryData<AppNotification[]>([NOTIFICATIONS_QUERY_KEY]);

      queryClient.setQueryData<AppNotification[]>([NOTIFICATIONS_QUERY_KEY], (old) =>
        old ? old.map((n) => (keys.includes(n.key ?? n.type) ? { ...n, seen: true } : n)) : old
      );

      return { previous };
    },
    onError: (_err, _types, context) => {
      if (context?.previous) {
        queryClient.setQueryData([NOTIFICATIONS_QUERY_KEY], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });

  return { notifications, unreadCount, isLoading, markSeen };
};
